import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApi from '../api';
import { useUser } from "../user/UserContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import AimdocLoader from "../ui/AimdocLoader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
    ArrowLeft, 
    Edit, 
    Trash2, 
    Copy, 
    CheckCircle, 
    XCircle, 
    AlertCircle,
    Loader2,
    Play,
    Globe,
    Calendar,
    Activity
} from "lucide-react";
import WebhookModal from './WebhookModal';
import { formatDate } from '../utils/dateFormatter';
import { WebhookEvents, WEBHOOK_EVENT_DESCRIPTIONS, WEBHOOK_EVENT_DISPLAY_NAMES } from './webhookEvents';

interface WebhookConfig {
    id: string;
    name: string;
    target_url: string;
    auth_secret?: string;
    has_auth_secret?: boolean;
    is_active: boolean;
    events: WebhookEvents;
    created_at: string;
    updated_at: string;
}

interface TestResult {
    event_type: string;
    success: boolean;
    message: string;
    status_code?: number;
    response?: string;
    error?: string;
}

const WebhookDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { get, post, put, remove } = useApi();
    const { organization } = useUser();
    
    const [webhook, setWebhook] = useState<WebhookConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [testingEvents, setTestingEvents] = useState<Set<string>>(new Set());
    const [testResults, setTestResults] = useState<Record<string, TestResult>>({});
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [savingWebhook, setSavingWebhook] = useState(false);
    const [deletingWebhook, setDeletingWebhook] = useState(false);
    const [samplePayloads, setSamplePayloads] = useState<Record<string, string>>({});
    const [loadingSamples, setLoadingSamples] = useState(false);

    const eventDescriptions = WEBHOOK_EVENT_DESCRIPTIONS;

    const eventDisplayNames = WEBHOOK_EVENT_DISPLAY_NAMES;

    const fetchWebhook = async () => {
        try {
            setLoading(true);
            const response = await get(`/hooks/external/${id}`);
            const webhookData = response.data;
            
            if (webhookData) {
                // The webhook data from the API already has the correct structure
                setWebhook(webhookData);
            } else {
                toast.error('Webhook not found');
                navigate('/integrations');
            }
        } catch (error: any) {
            console.error('Failed to fetch webhook:', error);
            if (error?.response?.status === 404) {
                toast.error('Webhook not found');
            } else {
                toast.error('Failed to load webhook');
            }
            navigate('/integrations');
        } finally {
            setLoading(false);
        }
    };

    const handleTestEvent = async (eventType: string) => {
        if (!webhook) return;
        
        setTestingEvents(prev => new Set([...prev, eventType]));
        setTestResults(prev => ({ ...prev, [eventType]: { event_type: eventType, success: false, message: 'Testing...' } }));
        
        try {
            const response = await post(`/hooks/external/${webhook.id}/test`, {
                event_type: eventType
            });
            
            const result: TestResult = {
                event_type: eventType,
                success: response.data.success,
                message: response.data.message,
                status_code: response.data.status_code,
                response: response.data.response,
                error: response.data.error
            };
            
            setTestResults(prev => ({ ...prev, [eventType]: result }));
            
            if (result.success) {
                toast.success(`Test ${eventType.replace(/_/g, ' ')} sent successfully!`);
            } else {
                toast.error(`Failed to send test ${eventType.replace(/_/g, ' ')}: ${result.message}`);
            }
        } catch (error: any) {
            const errorMessage = error?.response?.data?.detail || 'Failed to send test webhook';
            setTestResults(prev => ({ 
                ...prev, 
                [eventType]: { 
                    event_type: eventType, 
                    success: false, 
                    message: errorMessage,
                    error: error.toString()
                } 
            }));
            toast.error(errorMessage);
        } finally {
            setTestingEvents(prev => {
                const newSet = new Set(prev);
                newSet.delete(eventType);
                return newSet;
            });
        }
    };

    const handleSaveWebhook = async (webhookData: Partial<WebhookConfig>) => {
        setSavingWebhook(true);
        try {
            const response = await put(`/hooks/external/${id}`, webhookData);
            toast.success('Webhook updated successfully');
            setEditModalOpen(false);
            await fetchWebhook();
            return response.data;
        } catch (error) {
            toast.error('Failed to update webhook');
            console.error('Failed to update webhook:', error);
            return null;
        } finally {
            setSavingWebhook(false);
        }
    };

    const handleDeleteWebhook = async () => {
        if (!confirm('Are you sure you want to delete this webhook?')) return;
        
        setDeletingWebhook(true);
        try {
            await remove(`/hooks/external/${id}`);
            toast.success('Webhook deleted successfully');
            navigate('/integrations');
        } catch (error) {
            toast.error('Failed to delete webhook');
            console.error('Failed to delete webhook:', error);
        } finally {
            setDeletingWebhook(false);
        }
    };

    const copyPayload = async (eventType: string) => {
        try {
            let formattedPayload = samplePayloads[eventType];
            if (!formattedPayload) {
                const response = await get(`/hooks/samples/${eventType}`);
                formattedPayload = JSON.stringify(response.data, null, 2);
            }

            await navigator.clipboard.writeText(formattedPayload);
            toast.success(`${eventType.replace(/_/g, ' ')} payload copied to clipboard`);
        } catch (error) {
            console.error('Failed to copy payload:', error);
            toast.error('Failed to copy payload');
        }
    };

    const fetchAllSamplePayloads = async () => {
        if (Object.keys(samplePayloads).length > 0 || loadingSamples) return;
        setLoadingSamples(true);
        try {
            const entries = await Promise.all(
                Object.keys(eventDescriptions).map(async (event) => {
                    try {
                        const response = await get(`/hooks/samples/${event}`);
                        return [event, JSON.stringify(response.data, null, 2)] as const;
                    } catch (error) {
                        console.error(`Failed to load sample for ${event}:`, error);
                        return [event, ''] as const;
                    }
                })
            );
            const next: Record<string, string> = {};
            for (const [event, json] of entries) {
                if (json) next[event] = json;
            }
            setSamplePayloads(next);
        } finally {
            setLoadingSamples(false);
        }
    };

    useEffect(() => {
        if (id && organization?.id) {
            fetchWebhook();
            fetchAllSamplePayloads();
        }
    }, [id, organization]);

    if (loading) {
        return <AimdocLoader label="Loading webhook" />;
    }

    if (!webhook) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <AlertCircle className="h-12 w-12 text-muted-foreground" />
                <p className="text-muted-foreground">Webhook not found</p>
                <Button onClick={() => navigate('/integrations')}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Integrations
                </Button>
            </div>
        );
    }

    const activeEvents = Object.entries(webhook.events)
        .filter(([_, enabled]) => enabled)
        .map(([event]) => event);

    return (
        <div className="w-full">
            {/* Back Button */}
            <div className="pt-4 pl-4">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/integrations')}
                >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Integrations
                </Button>
            </div>

            <div className="container mx-auto pt-4 pb-8 px-4 max-w-6xl">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-3">
                        {webhook.name}
                        <Badge variant={webhook.is_active ? "default" : "secondary"}>
                            {webhook.is_active ? "Active" : "Inactive"}
                        </Badge>
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        Webhook ID: {webhook.id}
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={() => setEditModalOpen(true)}
                    >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleDeleteWebhook}
                        disabled={deletingWebhook}
                    >
                        {deletingWebhook ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <Trash2 className="h-4 w-4 mr-2" />
                        )}
                        Delete
                    </Button>
                </div>
            </div>

            {/* Overview Card */}
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle>Configuration</CardTitle>
                    <CardDescription>Webhook endpoint and basic settings</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Endpoint URL</Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    value={webhook.target_url}
                                    readOnly
                                    className="font-mono text-sm"
                                />
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        navigator.clipboard.writeText(webhook.target_url);
                                        toast.success('URL copied to clipboard');
                                    }}
                                >
                                    <Copy className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Status</Label>
                            <div className="flex items-center gap-2">
                                {webhook.is_active ? (
                                    <>
                                        <CheckCircle className="h-5 w-5 text-green-500" />
                                        <span className="text-sm">Webhook is active and will receive events</span>
                                    </>
                                ) : (
                                    <>
                                        <XCircle className="h-5 w-5 text-red-500" />
                                        <span className="text-sm">Webhook is inactive and won't receive events</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    <Separator />
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center gap-2">
                            <Activity className="h-4 w-4 text-muted-foreground" />
                            <span className="text-muted-foreground">Events:</span>
                            <span className="font-medium">{activeEvents.length} active</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span className="text-muted-foreground">Created:</span>
                            <span className="font-medium">
                                {formatDate(webhook.created_at, 'short')}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Globe className="h-4 w-4 text-muted-foreground" />
                            <span className="text-muted-foreground">Type:</span>
                            <span className="font-medium">Custom Webhook</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <AlertCircle className="h-4 w-4 text-muted-foreground" />
                            <span className="text-muted-foreground">Auth:</span>
                            <span className="font-medium">
                                {webhook.has_auth_secret ? 'Bearer token configured' : 'None'}
                            </span>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Events and Testing */}
            <Tabs defaultValue="events" className="space-y-4">
                <TabsList className="grid w-full grid-cols-2 max-w-[400px]">
                    <TabsTrigger value="events">Events & Testing</TabsTrigger>
                    <TabsTrigger value="payloads">Sample Payloads</TabsTrigger>
                </TabsList>
                
                <TabsContent value="events" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Subscribed Events</CardTitle>
                            <CardDescription>
                                Test individual events to verify your webhook endpoint is working correctly
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {Object.entries(eventDescriptions).map(([event, description]) => {
                                    const isSubscribed = webhook.events[event as keyof typeof webhook.events];
                                    const testResult = testResults[event];
                                    const isTesting = testingEvents.has(event);
                                    
                                    return (
                                        <div
                                            key={event}
                                            className={`p-4 rounded-lg border ${
                                                !isSubscribed ? 'bg-muted/50 opacity-60' : ''
                                            }`}
                                        >
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h4 className="font-medium">
                                                            {eventDisplayNames[event] || event.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                        </h4>
                                                        {isSubscribed ? (
                                                            <Badge variant="outline" className="text-xs">
                                                                Subscribed
                                                            </Badge>
                                                        ) : (
                                                            <Badge variant="secondary" className="text-xs">
                                                                Not Subscribed
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-muted-foreground">
                                                        {description}
                                                    </p>
                                                    
                                                    {(testResult || isTesting) && (
                                                        <div className="mt-2">
                                                            <Alert>
                                                                <AlertDescription className="text-xs">
                                                                    <div className="flex items-center gap-2">
                                                                        {isTesting ? (
                                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                                        ) : testResult?.success ? (
                                                                            <CheckCircle className="h-4 w-4 text-green-600" />
                                                                        ) : (
                                                                            <XCircle className="h-4 w-4 text-red-600" />
                                                                        )}
                                                                        <span>{isTesting ? 'Testing...' : testResult?.message}</span>
                                                                        {!isTesting && testResult?.status_code && (
                                                                            <Badge 
                                                                                variant="outline" 
                                                                                className={`text-xs ml-auto ${
                                                                                    testResult.success ? 'text-green-600' : 'text-red-600'
                                                                                }`}
                                                                            >
                                                                                {testResult.status_code}
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                </AlertDescription>
                                                            </Alert>
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleTestEvent(event)}
                                                    disabled={!isSubscribed || isTesting}
                                                >
                                                    {isTesting ? (
                                                        <>
                                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                            Testing...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Play className="h-4 w-4 mr-2" />
                                                            Test Event
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
                
                <TabsContent value="payloads" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Sample Payloads</CardTitle>
                            <CardDescription>
                                Example JSON payloads for each event type
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {Object.entries(eventDescriptions).map(([event, description]) => {
                                    const isSubscribed = webhook.events[event as keyof typeof webhook.events];
                                    const payloadJson = samplePayloads[event];
                                    const displayName = eventDisplayNames[event] || event.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

                                    return (
                                        <Card
                                            key={event}
                                            className={`flex flex-col ${!isSubscribed ? 'opacity-60' : ''}`}
                                        >
                                            <CardHeader className="pb-3 space-y-2">
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="min-w-0 flex-1">
                                                        <CardTitle className="text-sm leading-tight">
                                                            {displayName}
                                                        </CardTitle>
                                                        <CardDescription className="text-xs mt-1">
                                                            {description}
                                                        </CardDescription>
                                                    </div>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => copyPayload(event)}
                                                        disabled={!isSubscribed || !payloadJson}
                                                        className="shrink-0"
                                                    >
                                                        <Copy className="h-4 w-4 mr-1" />
                                                        Copy
                                                    </Button>
                                                </div>
                                            </CardHeader>
                                            <CardContent className="pt-0 flex-1">
                                                <div className="relative">
                                                    <pre className="text-xs font-mono bg-muted/50 rounded-md border p-3 overflow-auto max-h-64 whitespace-pre">
{payloadJson
    ? payloadJson
    : loadingSamples
        ? 'Loading sample payload...'
        : 'No sample payload available.'}
                                                    </pre>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Edit Modal */}
            {editModalOpen && (
                <WebhookModal
                    open={editModalOpen}
                    onOpenChange={setEditModalOpen}
                    webhook={webhook}
                    onSave={handleSaveWebhook}
                    saving={savingWebhook}
                />
            )}
            </div>
        </div>
    );
};

export default WebhookDetail;
